import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { Task, Project, GlobalLink, TaskStatus, SubTask, TaskComment, TaskLink, ActiveView, DiaryEntry, Note, FocusSession, CanvasWidget, WidgetType, FloatingMenuState, Page, AccessibilityPreferences } from '../types'

export type DashboardWidgetId = 'stats' | 'completion-chart' | 'today-tasks' | 'overdue' | 'distribution'

const DEFAULT_DASHBOARD_LAYOUT: DashboardWidgetId[] = [
  'stats',
  'completion-chart',
  'today-tasks',
  'overdue',
  'distribution',
]

const DEFAULT_SIDEBAR_ORDER: ActiveView[] = [
  'dashboard',
  'tasks',
  'kanban',
  'gantt',
  'priority-matrix',
  'links',
  'day-planner',
  'diary',
  'notes',
  'progress',
  'task-graph',
]

const WIDGET_DEFAULTS: Record<WidgetType, { width: number; height: number }> = {
  stats: { width: 520, height: 140 },
  'completion-chart': { width: 320, height: 340 },
  'today-tasks': { width: 360, height: 400 },
  'task-list': { width: 540, height: 460 },
  kanban: { width: 740, height: 440 },
  gantt: { width: 820, height: 360 },
  'priority-matrix': { width: 620, height: 500 },
  'day-planner': { width: 520, height: 460 },
  links: { width: 440, height: 400 },
  diary: { width: 440, height: 440 },
  notes: { width: 400, height: 420 },
  pomodoro: { width: 300, height: 300 },
  overdue: { width: 420, height: 300 },
  distribution: { width: 360, height: 240 },
  progress: { width: 720, height: 420 },
  'task-graph': { width: 820, height: 560 },
}

interface TaskStore {
  tasks: Task[]
  projects: Project[]
  links: GlobalLink[]
  diaryEntries: DiaryEntry[]
  notes: Note[]
  focusSessions: FocusSession[]
  dashboardLayout: DashboardWidgetId[]
  sidebarOrder: ActiveView[]
  pages: Page[]
  currentPageId: string
  maxZ: number
  selectedTaskId: string | null
  floatingMenuState: FloatingMenuState
  accessibility: AccessibilityPreferences

  addTask: (task: Omit<Task, 'id' | 'createdAt'>) => void
  importTasks: (tasks: Array<Omit<Task, 'id' | 'createdAt'>>) => number
  updateTask: (id: string, updates: Partial<Task>) => void
  deleteTask: (id: string) => void
  moveTask: (id: string, status: TaskStatus) => void
  toggleToday: (id: string) => void

  addSubTask: (taskId: string, title: string) => void
  updateSubTask: (taskId: string, subId: string, updates: Partial<SubTask>) => void
  deleteSubTask: (taskId: string, subId: string) => void
  addTaskComment: (taskId: string, text: string) => void
  deleteTaskComment: (taskId: string, commentId: string) => void

  addTaskLink: (taskId: string, link: Omit<TaskLink, 'id'>) => void
  deleteTaskLink: (taskId: string, linkId: string) => void

  addProject: (project: Omit<Project, 'id'>) => void
  updateProject: (id: string, updates: Partial<Project>) => void
  deleteProject: (id: string) => void

  addLink: (link: Omit<GlobalLink, 'id' | 'createdAt'>) => void
  updateLink: (id: string, updates: Partial<GlobalLink>) => void
  deleteLink: (id: string) => void

  reorderDashboard: (order: DashboardWidgetId[]) => void
  reorderSidebar: (order: ActiveView[]) => void

  addCanvasWidget: (type: WidgetType, x?: number, y?: number) => void
  removeCanvasWidget: (instanceId: string) => void
  updateCanvasWidget: (instanceId: string, updates: Partial<CanvasWidget>) => void
  bringToFront: (instanceId: string) => void
  clearCanvas: () => void
  arrangeCanvasWidgets: (widgets: Array<Pick<CanvasWidget, 'instanceId' | 'x' | 'y' | 'width' | 'height'>>) => void

  addDiaryEntry: (entry: Omit<DiaryEntry, 'id' | 'createdAt'>) => void
  updateDiaryEntry: (id: string, updates: Partial<DiaryEntry>) => void
  deleteDiaryEntry: (id: string) => void

  addNote: (note: Omit<Note, 'id' | 'createdAt'>) => void
  updateNote: (id: string, updates: Partial<Note>) => void
  deleteNote: (id: string) => void
  addFocusSession: (session: Omit<FocusSession, 'id'>) => void

  selectTask: (id: string | null) => void
  updateFloatingMenuState: (updates: Partial<FloatingMenuState>) => void
  setCurrentPage: (pageId: string) => void
  addPage: (name: string) => void
  deletePage: (pageId: string) => void
  renamePage: (pageId: string, name: string) => void
  getCanvasWidgets: () => CanvasWidget[]
  exportData: () => string
  importData: (json: string) => boolean
  hydrateData: (data: Partial<Pick<TaskStore, 'tasks' | 'projects' | 'links' | 'diaryEntries' | 'notes' | 'focusSessions' | 'pages' | 'currentPageId' | 'dashboardLayout' | 'sidebarOrder' | 'floatingMenuState' | 'accessibility'>>) => void
  clearUserData: () => void
  updateAccessibility: (updates: Partial<AccessibilityPreferences>) => void
}

const uid = () => crypto.randomUUID()
const normalizeTask = <T extends { comments?: TaskComment[] }>(task: T): T & { comments: TaskComment[] } => ({ ...task, comments: Array.isArray(task.comments) ? task.comments : [] })

export const legacySampleTasks: Task[] = [
  {
    id: uid(),
    title: 'Revisar relatório mensal',
    description: 'Analisar métricas e preparar apresentação',
    status: 'in-progress',
    priority: 'high',
    effort: 3,
    dueDate: new Date().toISOString().split('T')[0],
    startDate: new Date(Date.now() - 86400000 * 2).toISOString().split('T')[0],
    isToday: true,
    comments: [],
    subtasks: [
      { id: uid(), title: 'Coletar dados', status: 'done' },
      { id: uid(), title: 'Criar gráficos', status: 'in-progress' },
      { id: uid(), title: 'Escrever resumo', status: 'todo' },
    ],
    links: [],
    projectId: '',
    tags: ['relatório', 'mensal'],
    createdAt: new Date().toISOString(),
  },
  {
    id: uid(),
    title: 'Implementar autenticação',
    description: 'Adicionar login com JWT no backend',
    status: 'todo',
    priority: 'urgent',
    effort: 5,
    dueDate: new Date(Date.now() + 86400000 * 3).toISOString().split('T')[0],
    startDate: new Date().toISOString().split('T')[0],
    isToday: false,
    comments: [],
    subtasks: [
      { id: uid(), title: 'Configurar JWT', status: 'todo' },
      { id: uid(), title: 'Criar endpoints de login', status: 'todo' },
      { id: uid(), title: 'Testes de integração', status: 'todo' },
    ],
    links: [],
    projectId: '',
    tags: ['backend', 'segurança'],
    createdAt: new Date().toISOString(),
  },
  {
    id: uid(),
    title: 'Design da landing page',
    description: 'Criar mockups e protótipos no Figma',
    status: 'done',
    priority: 'medium',
    effort: 2,
    dueDate: new Date(Date.now() - 86400000).toISOString().split('T')[0],
    startDate: new Date(Date.now() - 86400000 * 5).toISOString().split('T')[0],
    isToday: false,
    comments: [],
    subtasks: [
      { id: uid(), title: 'Wireframes', status: 'done' },
      { id: uid(), title: 'Protótipo interativo', status: 'done' },
    ],
    links: [],
    projectId: '',
    tags: ['design', 'frontend'],
    createdAt: new Date().toISOString(),
  },
]

export const legacySampleProjects: Project[] = [
  { id: uid(), name: 'Projeto Alpha', color: '#3b82f6' },
  { id: uid(), name: 'Projeto Beta', color: '#10b981' },
]

const sampleLinks: GlobalLink[] = []
const sampleDiaryEntries: DiaryEntry[] = []
const sampleNotes: Note[] = []

const DEFAULT_ACCESSIBILITY: AccessibilityPreferences = {
  reducedMotion: false,
  calmVisuals: false,
  largeText: false,
  compactMode: false,
  simpleNavigation: false,
  darkMode: false,
}

const defaultPageId = uid()
const samplePages: Page[] = [
  { id: defaultPageId, name: 'Dashboard', canvasWidgets: [], createdAt: new Date().toISOString() },
]

export const useTaskStore = create<TaskStore>()(
  persist(
    (set) => ({
      tasks: [],
      projects: [],
      links: sampleLinks,
      diaryEntries: sampleDiaryEntries,
      notes: sampleNotes,
      focusSessions: [],
      dashboardLayout: DEFAULT_DASHBOARD_LAYOUT,
      sidebarOrder: DEFAULT_SIDEBAR_ORDER,
      pages: samplePages,
      currentPageId: defaultPageId,
      maxZ: 10,
      selectedTaskId: null,
      floatingMenuState: { x: 24, y: 400, width: 280, height: 400, open: false },
      accessibility: DEFAULT_ACCESSIBILITY,

      addTask: (task) =>
        set((s) => ({
          tasks: [
            ...s.tasks,
            { ...normalizeTask(task), completedAt: task.status === 'done' ? task.completedAt ?? new Date().toISOString() : undefined, id: uid(), createdAt: new Date().toISOString() },
          ],
        })),

      importTasks: (incoming) => {
        const existing = new Set(useTaskStore.getState().tasks.map((task) => `${task.title.trim().toLowerCase()}|${task.dueDate ?? ''}`))
        const additions = incoming.filter((task) => {
          const key = `${task.title.trim().toLowerCase()}|${task.dueDate ?? ''}`
          if (!task.title.trim() || existing.has(key)) return false
          existing.add(key)
          return true
        }).map((task) => ({ ...normalizeTask(task), id: uid(), createdAt: new Date().toISOString(), completedAt: task.status === 'done' ? task.completedAt ?? new Date().toISOString() : undefined }))
        if (additions.length) set((state) => ({ tasks: [...state.tasks, ...additions] }))
        return additions.length
      },

      updateTask: (id, updates) =>
        set((s) => ({
          tasks: s.tasks.map((t) => t.id === id ? {
            ...t,
            ...updates,
            completedAt: updates.status === 'done' ? t.completedAt ?? new Date().toISOString() : updates.status ? undefined : t.completedAt,
          } : t),
        })),

      deleteTask: (id) =>
        set((s) => ({ tasks: s.tasks.filter((t) => t.id !== id) })),

      moveTask: (id, status) =>
        set((s) => ({
          tasks: s.tasks.map((t) => (t.id === id ? { ...t, status, completedAt: status === 'done' ? t.completedAt ?? new Date().toISOString() : undefined } : t)),
        })),

      toggleToday: (id) =>
        set((s) => ({
          tasks: s.tasks.map((t) =>
            t.id === id ? { ...t, isToday: !t.isToday } : t
          ),
        })),

      addSubTask: (taskId, title) =>
        set((s) => ({
          tasks: s.tasks.map((t) =>
            t.id === taskId
              ? {
                  ...t,
                  subtasks: [
                    ...t.subtasks,
                    { id: uid(), title, status: 'todo' },
                  ],
                }
              : t
          ),
        })),

      updateSubTask: (taskId, subId, updates) =>
        set((s) => ({
          tasks: s.tasks.map((t) =>
            t.id === taskId
              ? {
                  ...t,
                  subtasks: t.subtasks.map((st) =>
                    st.id === subId ? { ...st, ...updates } : st
                  ),
                }
              : t
          ),
        })),

      deleteSubTask: (taskId, subId) =>
        set((s) => ({
          tasks: s.tasks.map((t) =>
            t.id === taskId
              ? { ...t, subtasks: t.subtasks.filter((st) => st.id !== subId) }
              : t
          ),
        })),

      addTaskComment: (taskId, text) =>
        set((s) => ({ tasks: s.tasks.map((t) => t.id === taskId ? { ...t, comments: [...(t.comments ?? []), { id: uid(), text, authorLabel: 'Você', createdAt: new Date().toISOString() }] } : t) })),

      deleteTaskComment: (taskId, commentId) =>
        set((s) => ({ tasks: s.tasks.map((t) => t.id === taskId ? { ...t, comments: (t.comments ?? []).filter((comment) => comment.id !== commentId) } : t) })),

      addTaskLink: (taskId, link) =>
        set((s) => ({
          tasks: s.tasks.map((t) =>
            t.id === taskId
              ? { ...t, links: [...t.links, { ...link, id: uid() }] }
              : t
          ),
        })),

      deleteTaskLink: (taskId, linkId) =>
        set((s) => ({
          tasks: s.tasks.map((t) =>
            t.id === taskId
              ? { ...t, links: t.links.filter((l) => l.id !== linkId) }
              : t
          ),
        })),

      addProject: (project) =>
        set((s) => ({
          projects: [...s.projects, { ...project, id: uid() }],
        })),

      updateProject: (id, updates) =>
        set((s) => ({
          projects: s.projects.map((p) =>
            p.id === id ? { ...p, ...updates } : p
          ),
        })),

      deleteProject: (id) =>
        set((s) => ({
          projects: s.projects.filter((p) => p.id !== id),
        })),

      addLink: (link) =>
        set((s) => ({
          links: [
            ...s.links,
            { ...link, id: uid(), createdAt: new Date().toISOString() },
          ],
        })),

      updateLink: (id, updates) =>
        set((s) => ({
          links: s.links.map((l) => (l.id === id ? { ...l, ...updates } : l)),
        })),

      deleteLink: (id) =>
        set((s) => ({ links: s.links.filter((l) => l.id !== id) })),

      reorderDashboard: (order) => set({ dashboardLayout: order }),
      reorderSidebar: (order) => set({ sidebarOrder: order }),

      addCanvasWidget: (type, x, y) =>
        set((s) => {
          const newZ = s.maxZ + 1
          const defaults = WIDGET_DEFAULTS[type] ?? { width: 400, height: 300 }
          const pageIndex = s.pages.findIndex((p) => p.id === s.currentPageId)
          if (pageIndex === -1) return s
          const newPages = [...s.pages]
          newPages[pageIndex] = {
            ...newPages[pageIndex],
            canvasWidgets: [
              ...newPages[pageIndex].canvasWidgets,
              {
                instanceId: uid(),
                type,
                x: x ?? 80 + Math.random() * 120,
                y: y ?? 80 + Math.random() * 120,
                width: defaults.width,
                height: defaults.height,
                minimized: false,
                zIndex: newZ,
              },
            ],
          }
          return { maxZ: newZ, pages: newPages }
        }),

      removeCanvasWidget: (instanceId) =>
        set((s) => {
          const pageIndex = s.pages.findIndex((p) => p.id === s.currentPageId)
          if (pageIndex === -1) return s
          const newPages = [...s.pages]
          newPages[pageIndex] = {
            ...newPages[pageIndex],
            canvasWidgets: newPages[pageIndex].canvasWidgets.filter((w) => w.instanceId !== instanceId),
          }
          return { pages: newPages }
        }),

      updateCanvasWidget: (instanceId, updates) =>
        set((s) => {
          const pageIndex = s.pages.findIndex((p) => p.id === s.currentPageId)
          if (pageIndex === -1) return s
          const newPages = [...s.pages]
          newPages[pageIndex] = {
            ...newPages[pageIndex],
            canvasWidgets: newPages[pageIndex].canvasWidgets.map((w) =>
              w.instanceId === instanceId ? { ...w, ...updates } : w
            ),
          }
          return { pages: newPages }
        }),

      bringToFront: (instanceId) =>
        set((s) => {
          const newZ = s.maxZ + 1
          const pageIndex = s.pages.findIndex((p) => p.id === s.currentPageId)
          if (pageIndex === -1) return s
          const newPages = [...s.pages]
          newPages[pageIndex] = {
            ...newPages[pageIndex],
            canvasWidgets: newPages[pageIndex].canvasWidgets.map((w) =>
              w.instanceId === instanceId ? { ...w, zIndex: newZ } : w
            ),
          }
          return { maxZ: newZ, pages: newPages }
        }),

      clearCanvas: () =>
        set((s) => {
          const pageIndex = s.pages.findIndex((p) => p.id === s.currentPageId)
          if (pageIndex === -1) return s
          const newPages = [...s.pages]
          newPages[pageIndex] = { ...newPages[pageIndex], canvasWidgets: [] }
          return { pages: newPages }
        }),

      arrangeCanvasWidgets: (layouts) =>
        set((s) => {
          const pageIndex = s.pages.findIndex((p) => p.id === s.currentPageId)
          if (pageIndex === -1) return s
          const byId = new Map(layouts.map((layout) => [layout.instanceId, layout]))
          const pages = [...s.pages]
          pages[pageIndex] = { ...pages[pageIndex], canvasWidgets: pages[pageIndex].canvasWidgets.map((widget) => ({ ...widget, ...byId.get(widget.instanceId) })) }
          return { pages }
        }),

      addDiaryEntry: (entry) =>
        set((s) => ({
          diaryEntries: [
            { ...entry, id: uid(), createdAt: new Date().toISOString() },
            ...s.diaryEntries,
          ],
        })),
      updateDiaryEntry: (id, updates) =>
        set((s) => ({
          diaryEntries: s.diaryEntries.map((e) => (e.id === id ? { ...e, ...updates } : e)),
        })),
      deleteDiaryEntry: (id) =>
        set((s) => ({ diaryEntries: s.diaryEntries.filter((e) => e.id !== id) })),

      addNote: (note) =>
        set((s) => ({
          notes: [{ ...note, id: uid(), createdAt: new Date().toISOString() }, ...s.notes],
        })),
      updateNote: (id, updates) =>
        set((s) => ({
          notes: s.notes.map((n) => (n.id === id ? { ...n, ...updates } : n)),
        })),
      deleteNote: (id) =>
        set((s) => ({ notes: s.notes.filter((n) => n.id !== id) })),

      selectTask: (id) => set({ selectedTaskId: id }),

      updateFloatingMenuState: (updates) =>
        set((s) => ({
          floatingMenuState: { ...s.floatingMenuState, ...updates },
        })),

      addFocusSession: (session) => set((s) => ({ focusSessions: [{ ...session, id: uid() }, ...s.focusSessions] })),

      setCurrentPage: (pageId) =>
        set((s) => {
          if (s.pages.find((p) => p.id === pageId)) {
            return { currentPageId: pageId }
          }
          return s
        }),

      addPage: (name) =>
        set((s) => ({
          pages: [
            ...s.pages,
            { id: uid(), name, canvasWidgets: [], createdAt: new Date().toISOString() },
          ],
        })),

      deletePage: (pageId) =>
        set((s) => {
          const newPages = s.pages.filter((p) => p.id !== pageId)
          if (newPages.length === 0) return s
          return {
            pages: newPages,
            currentPageId: s.currentPageId === pageId ? newPages[0].id : s.currentPageId,
          }
        }),

      renamePage: (pageId, name) =>
        set((s) => ({
          pages: s.pages.map((p) => (p.id === pageId ? { ...p, name } : p)),
        })),

      getCanvasWidgets: () => {
        const state = useTaskStore.getState() as any
        const currentPage = state.pages.find((p: any) => p.id === state.currentPageId)
        return currentPage?.canvasWidgets ?? []
      },

      exportData: (): string => {
        const state = useTaskStore.getState() as any
        return JSON.stringify({
          tasks: state.tasks,
          projects: state.projects,
          links: state.links,
          diaryEntries: state.diaryEntries,
          notes: state.notes,
          focusSessions: state.focusSessions,
          pages: state.pages,
          currentPageId: state.currentPageId,
          dashboardLayout: state.dashboardLayout,
          sidebarOrder: state.sidebarOrder,
          floatingMenuState: state.floatingMenuState,
        }, null, 2)
      },

      importData: (json: any): boolean => {
        try {
          const data = JSON.parse(json)

          // Detectar formato antigo (antes de páginas)
          let pages = data.pages
          let currentPageId = data.currentPageId

          if (!pages && data.canvasWidgets) {
            // Migrar formato antigo para novo
            const pageId = uid()
            pages = [
              {
                id: pageId,
                name: 'Dashboard',
                canvasWidgets: data.canvasWidgets,
                createdAt: new Date().toISOString()
              }
            ]
            currentPageId = pageId
          } else if (!pages) {
            pages = [{ ...samplePages[0], id: uid(), canvasWidgets: [] }]
            currentPageId = defaultPageId
          }

          set({
            tasks: (data.tasks ?? []).map(normalizeTask),
            projects: data.projects ?? [],
            links: data.links ?? [],
            diaryEntries: data.diaryEntries ?? [],
            notes: data.notes ?? [],
            focusSessions: data.focusSessions ?? [],
            pages,
            currentPageId,
            dashboardLayout: data.dashboardLayout ?? DEFAULT_DASHBOARD_LAYOUT,
            sidebarOrder: data.sidebarOrder ?? DEFAULT_SIDEBAR_ORDER,
            floatingMenuState: data.floatingMenuState ?? { x: 24, y: 400, width: 280, height: 400, open: false },
            accessibility: data.accessibility ?? DEFAULT_ACCESSIBILITY,
          })
          return true
        } catch {
          return false
        }
      },
      hydrateData: (data) => set((state) => ({
        ...data,
        tasks: data.tasks?.map(normalizeTask),
        focusSessions: data.focusSessions ?? state.focusSessions,
        currentPageId: data.currentPageId ?? data.pages?.[0]?.id ?? state.currentPageId,
      })),
      clearUserData: () => {
        const pageId = uid()
        set({
          tasks: [], projects: [], links: [], diaryEntries: [], notes: [], focusSessions: [],
          pages: [{ id: pageId, name: 'Dashboard', canvasWidgets: [], createdAt: new Date().toISOString() }],
          currentPageId: pageId, selectedTaskId: null,
        })
      },
      updateAccessibility: (updates) => set((state) => ({ accessibility: { ...state.accessibility, ...updates } })),
    }),
    {
      name: 'task-priority-store',
      version: 4,
      migrate: (persisted: any) => ({ ...persisted, tasks: (persisted.tasks ?? []).map(normalizeTask), focusSessions: persisted.focusSessions ?? [] }),
    }
  )
)

export function getDataSnapshot() {
  const state = useTaskStore.getState()
  return {
    tasks: state.tasks, projects: state.projects, links: state.links,
    diaryEntries: state.diaryEntries, notes: state.notes, focusSessions: state.focusSessions, pages: state.pages,
    currentPageId: state.currentPageId, dashboardLayout: state.dashboardLayout,
    sidebarOrder: state.sidebarOrder, floatingMenuState: state.floatingMenuState,
    accessibility: state.accessibility,
  }
}
